import React, { useState, useEffect } from 'react';
import { Link } from './Link';
import { useRouter } from './Router';
import { Logo } from './Logo';
import SEO from './SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ArrowRight, MessageSquare, Shield, ChevronRight, CheckCircle2,
    Clock, ArrowUpRight, Send, CheckCheck, Check, Copy
} from 'lucide-react';
import { LandingPageChatbot } from './LandingPageChatbot';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip } from 'recharts';

let msgSeq = 100;
function createMessageId(prefix: string): string {
    msgSeq += 1;
    return `${prefix}_${msgSeq}`;
}

function getFormattedTime(): string {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

// --- ARCHITECTURAL BACKGROUND (Obsidian Canvas with Atmospheric Light) ---

const ExecutiveBackground: React.FC = () => {
    return (
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden bg-[#07080B]">
            {/* Top Atmospheric Neutral Glow */}
            <div 
                className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[90vw] max-w-6xl h-[520px] rounded-full opacity-[0.06] blur-[150px] pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at center, #10B981 0%, #064E3B 40%, transparent 80%)' }}
            />
            {/* Secondary Accent Light */}
            <div 
                className="absolute top-[28%] right-[-12%] w-[550px] h-[550px] rounded-full opacity-[0.025] blur-[180px] pointer-events-none"
                style={{ background: '#38BDF8' }}
            />
        </div>
    );
};

// --- ENTERPRISE STATUS HEADER STRIP ---

const TopStatusBanner: React.FC = () => {
    return (
        <div className="w-full bg-[#0B0D14] border-b border-white/[0.06] py-1.5 px-4 text-[11px] font-mono text-zinc-400">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span>Meta Graph API v21.0 Online</span>
                    </span>
                    <span className="hidden sm:inline text-zinc-400">|</span>
                    <span className="hidden sm:inline text-zinc-300">Global Edge Latency &lt; 24ms</span>
                </div>
                <div className="flex items-center gap-4 text-zinc-300">
                    <span className="hidden md:inline">Intelligence: Latest Gemini Models</span>
                    <span>SOC-2 Type II Certified</span>
                </div>
            </div>
        </div>
    );
};

// --- ENTERPRISE STATS PILLARS WITH PRECISION CORNER CROSSHAIRS ---

const EnterpriseStatsStrip: React.FC = () => {
    const stats = [
        {
            value: "98.2%",
            label: "Average Open Rate",
            subtext: "Official Meta Cloud API tier vs. 21% industry email benchmark"
        },
        {
            value: "< 45s",
            label: "Automated Triage Speed",
            subtext: "Instant intent classification powered by the latest Gemini models"
        },
        {
            value: "0.00%",
            label: "Account Ban Risk",
            subtext: "100% native Meta Graph API v21.0 compliance without browser scrapers"
        },
        {
            value: "4.8x",
            label: "Pipeline Conversion Lift",
            subtext: "Documented average across first 30 days of active deployment"
        }
    ];

    return (
        <div className="w-full max-w-7xl mx-auto px-6 py-12 relative z-10">
            <div className="relative border border-white/[0.08] bg-[#0A0C13]/60 backdrop-blur-sm rounded-xl overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                {/* Crosshairs at grid corners */}
                <div className="absolute -top-1.5 -left-1.5 font-mono text-[10px] text-zinc-400 pointer-events-none select-none" aria-hidden="true">+</div>
                <div className="absolute -top-1.5 -right-1.5 font-mono text-[10px] text-zinc-400 pointer-events-none select-none" aria-hidden="true">+</div>
                <div className="absolute -bottom-1.5 -left-1.5 font-mono text-[10px] text-zinc-400 pointer-events-none select-none" aria-hidden="true">+</div>
                <div className="absolute -bottom-1.5 -right-1.5 font-mono text-[10px] text-zinc-400 pointer-events-none select-none" aria-hidden="true">+</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 sm:p-8">
                    {stats.map((stat, i) => (
                        <div key={i} className="flex flex-col justify-between hover:bg-white/[0.015] p-2 rounded-lg transition-colors">
                            <div className="font-mono text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-3">
                                {stat.value}
                            </div>
                            <div>
                                <div className="text-[11px] font-mono font-medium text-emerald-400 uppercase tracking-wider mb-1.5">
                                    {stat.label}
                                </div>
                                <div className="text-[13px] text-zinc-400 leading-relaxed font-normal">
                                    {stat.subtext}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- INTERACTIVE WORKBENCH: LIVE ARCHITECTURAL RUNTIME SIMULATOR ---

interface Scenario {
    id: string;
    tag: string;
    title: string;
    description: string;
    pipeline: Array<{
        id: string;
        name: string;
        type: 'webhook' | 'sheets' | 'gemini' | 'dispatch';
        latency: string;
        input: Record<string, any>;
        output: Record<string, any>;
    }>;
    chatSession: {
        customerName: string;
        customerPhone: string;
        avatarLetter: string;
        messages: Array<{
            id: string;
            sender: 'user' | 'agent' | 'system';
            text: string;
            time: string;
            status?: 'sent' | 'delivered' | 'read';
            quickReplies?: string[];
        }>;
    };
    codeExample: {
        typescript: string;
        python: string;
        curl: string;
    };
}

const WORKBENCH_SCENARIOS: Scenario[] = [
    {
        id: 'cart-recovery',
        tag: 'E-Commerce Recovery',
        title: 'Abandoned Checkout Recovery with Dynamic Pricing',
        description: 'Detects cart abandonment, queries inventory catalog via Google Sheets, and dispatches a personalized incentive via official WhatsApp template.',
        pipeline: [
            {
                id: 'step-1',
                name: 'Inbound Webhook Trigger',
                type: 'webhook',
                latency: '12ms',
                input: { event: 'checkout_abandoned', cart_id: 'cart_9921', total: '$189.00' },
                output: { phone: '+14155552671', customer: 'David Vance', items: ['Studio Headphones (Black)'] }
            },
            {
                id: 'step-2',
                name: 'Google Sheets CRM Sync',
                type: 'sheets',
                latency: '140ms',
                input: { operation: 'lookup', column: 'Phone', key: '+14155552671' },
                output: { tier: 'Repeat Buyer', discount_eligible: true, last_purchase: '2026-06-12' }
            },
            {
                id: 'step-3',
                name: 'Latest Gemini Model Intent & Reasoning',
                type: 'gemini',
                latency: '260ms',
                input: { engine: 'latest-gemini-models', intent: 'gentle_recovery', offer: '15% Off' },
                output: { template: 'CART15', tone: 'helpful', coupon_code: 'RESTORE15', confidence: 0.99 }
            },
            {
                id: 'step-4',
                name: 'Meta Cloud API Template Dispatch',
                type: 'dispatch',
                latency: '78ms',
                input: { template: 'ecom_cart_incentive_v2', lang: 'en_US' },
                output: { wamid: 'wamid.HBgLMTQxNTU1...AgA=', status: 'delivered' }
            }
        ],
        chatSession: {
            customerName: 'David Vance',
            customerPhone: '+1 (415) 555-2671',
            avatarLetter: 'D',
            messages: [
                {
                    id: 'm1',
                    sender: 'agent',
                    text: 'Hello David, we noticed you left the Studio Headphones in your cart. Would you like a 15% promotional code to complete your order today?',
                    time: '14:22',
                    status: 'read',
                    quickReplies: ['Yes, send code', 'Talk to human agent', 'Cancel order']
                },
                {
                    id: 'm2',
                    sender: 'user',
                    text: 'Yes please! Does the code apply to express shipping as well?',
                    time: '14:23'
                },
                {
                    id: 'm3',
                    sender: 'agent',
                    text: 'Yes! Use code RESTORE15 at checkout for 15% off your entire cart including express delivery. Here is your direct link: reach.link/c/cart_9921?code=RESTORE15',
                    time: '14:23',
                    status: 'read'
                }
            ]
        },
        codeExample: {
            typescript: `import { ReachClient } from '@bytecohort/reach';

const reach = new ReachClient({ apiKey: process.env.REACH_API_KEY });

// Trigger automated cart recovery pipeline
await reach.workflows.trigger('cart-recovery-v2', {
  recipient: '+14155552671',
  variables: {
    customer_name: 'David',
    cart_items: 'Studio Headphones (Black)',
    cart_url: 'https://reach.link/c/cart_9921'
  },
  aiAgent: {
    model: 'latest-gemini-models',
    autonomousReply: true,
    maxTurns: 5
  }
});`,
            python: `from bytecohort_reach import ReachClient
import os

reach = ReachClient(api_key=os.getenv("REACH_API_KEY"))

# Trigger automated cart recovery pipeline
response = reach.workflows.trigger(
    workflow_id="cart-recovery-v2",
    recipient="+14155552671",
    variables={
        "customer_name": "David",
        "cart_items": "Studio Headphones (Black)",
        "cart_url": "https://reach.link/c/cart_9921"
    },
    ai_agent={
        "model": "latest-gemini-models",
        "autonomous_reply": True,
        "max_turns": 5
    }
)`,
            curl: `curl -X POST https://api.bytecohortreach.com/v1/workflows/cart-recovery-v2/trigger \\
  -H "Authorization: Bearer $REACH_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipient": "+14155552671",
    "variables": { "customer_name": "David", "cart_url": "https://reach.link/c/cart_9921" },
    "aiAgent": { "model": "latest-gemini-models", "autonomousReply": true }
  }'`
        }
    },
    {
        id: 'patient-triage',
        tag: 'Healthcare Operations',
        title: 'Patient Directory Lookup & Appointment Triage',
        description: 'Authenticates patient records in under 300ms against encrypted Google Sheets directories, verifying insurance eligibility and scheduling doctor visits.',
        pipeline: [
            {
                id: 'step-1',
                name: 'Patient Message Ingest',
                type: 'webhook',
                latency: '18ms',
                input: { raw_text: 'Need to check my cardiology appointment for tomorrow', sender: '+447700900142' },
                output: { parsed_intent: 'appointment_query', phone: '+447700900142' }
            },
            {
                id: 'step-2',
                name: 'Secure Patient Sheet Query',
                type: 'sheets',
                latency: '175ms',
                input: { sheet: 'Clinical_Schedule_2026', match_mode: 'phone_e164' },
                output: { patient_name: 'Dr. Sarah Jenkins', doctor: 'Dr. Mensah', time: '10:30 AM BST', status: 'Confirmed' }
            },
            {
                id: 'step-3',
                name: 'Latest Gemini Model Clinical Guardrail',
                type: 'gemini',
                latency: '240ms',
                input: { engine: 'latest-gemini-models', check_urgency: true, triage_level: 'routine_confirmation' },
                output: { emergency: false, action: 'send_calendar_pass', confidence: 0.995 }
            },
            {
                id: 'step-4',
                name: 'Verified WhatsApp Dispatch',
                type: 'dispatch',
                latency: '82ms',
                input: { priority: 'high', security: 'hipaa_compliant_token' },
                output: { delivery_timestamp: '2026-09-04T14:25:01Z', status: 'delivered' }
            }
        ],
        chatSession: {
            customerName: 'Sarah Jenkins',
            customerPhone: '+44 (770) 090-0142',
            avatarLetter: 'S',
            messages: [
                {
                    id: 'm1',
                    sender: 'user',
                    text: 'Hi, I need to check my cardiology appointment for tomorrow. What time should I arrive?',
                    time: '09:14'
                },
                {
                    id: 'm2',
                    sender: 'agent',
                    text: 'Good morning Dr. Jenkins. Your appointment with Dr. Mensah is scheduled for tomorrow at 10:30 AM BST in Clinic Suite 4B. Please arrive 10 minutes early for check-in.',
                    time: '09:14',
                    status: 'read',
                    quickReplies: ['Confirm Attendance', 'Reschedule Slot', 'Request Directions']
                },
                {
                    id: 'm3',
                    sender: 'user',
                    text: 'Confirmed. Thank you!',
                    time: '09:15'
                },
                {
                    id: 'm4',
                    sender: 'agent',
                    text: 'Wonderful. Your check-in pass has been recorded to our front-desk system. We look forward to seeing you.',
                    time: '09:15',
                    status: 'read'
                }
            ]
        },
        codeExample: {
            typescript: `// Clinical Appointment Verification Handler
reach.webhooks.onMessage(async ({ message, sender }) => {
  const patient = await reach.sheets.lookup({
    sheetId: 'CLINICAL_ROSTER_2026',
    searchPhone: sender
  });

  const response = await reach.ai.generateResponse({
    model: 'latest-gemini-models',
    systemInstruction: 'You are a HIPAA-compliant medical clinic receptionist.',
    context: patient,
    userQuery: message.text
  });

  await reach.messages.sendText(sender, response.reply);
});`,
            python: `# Clinical Appointment Verification Handler
@reach.on_message
def handle_patient_inquiry(message, sender):
    patient = reach.sheets.lookup(
        sheet_id="CLINICAL_ROSTER_2026",
        phone=sender
    )
    
    reply = reach.ai.generate_reply(
        model="latest-gemini-models",
        system_instruction="HIPAA-compliant receptionist guardrails.",
        context=patient,
        query=message.text
    )
    
    reach.messages.send(recipient=sender, text=reply)`,
            curl: `curl -X POST https://api.bytecohortreach.com/v1/sheets/query \\
  -H "Authorization: Bearer $REACH_API_KEY" \\
  -d '{"sheetId": "CLINICAL_ROSTER_2026", "query": "+447700900142"}'`
        }
    },
    {
        id: 'real-estate',
        tag: 'High-Value Inbound',
        title: 'B2B & Real Estate Buyer Intent Scoring',
        description: 'Pre-qualifies inbound buyers by evaluating budget, timeframe, and asset requirements before notifying the designated senior sales director.',
        pipeline: [
            {
                id: 'step-1',
                name: 'Listing Inbound Trigger',
                type: 'webhook',
                latency: '15ms',
                input: { source: 'Portal_Campaign_Q3', campaign_id: 'listing_waterfront_7' },
                output: { property: 'The Waterfront Penthouse', budget_bracket: '$1.5M+' }
            },
            {
                id: 'step-2',
                name: 'Latest Gemini Model Intent & Budget Extraction',
                type: 'gemini',
                latency: '245ms',
                input: { engine: 'latest-gemini-models', extract_entities: ['budget', 'move_date', 'financing_status'] },
                output: { qualified: true, score: 94, readiness: 'Immediate Buyer', confidence: 0.98 }
            },
            {
                id: 'step-3',
                name: 'Google Sheets Pipeline Logging',
                type: 'sheets',
                latency: '160ms',
                input: { sheet: 'Q3_High_Ticket_Pipeline', action: 'append_row' },
                output: { row_index: 418, assigned_agent: 'Alexander Sterling' }
            },
            {
                id: 'step-4',
                name: 'Team Desk Handshake',
                type: 'dispatch',
                latency: '70ms',
                input: { internal_slack_alert: true, client_brochure: 'PDF_Secured' },
                output: { handoff_complete: true }
            }
        ],
        chatSession: {
            customerName: 'Marcus Sterling',
            customerPhone: '+1 (212) 555-0819',
            avatarLetter: 'M',
            messages: [
                {
                    id: 'm1',
                    sender: 'user',
                    text: 'Hello, looking for details on the Waterfront Penthouse. Can we view this Friday afternoon?',
                    time: '11:02'
                },
                {
                    id: 'm2',
                    sender: 'agent',
                    text: 'Hello Marcus, thank you for your interest in The Waterfront Penthouse. The property is available for private walkthroughs this Friday at 2:00 PM or 4:30 PM. Would either time suit your schedule?',
                    time: '11:02',
                    status: 'read',
                    quickReplies: ['Friday at 2:00 PM', 'Friday at 4:30 PM', 'Request Floorplan PDF']
                },
                {
                    id: 'm3',
                    sender: 'user',
                    text: 'Friday at 2:00 PM works perfectly. We are all-cash buyers.',
                    time: '11:03'
                },
                {
                    id: 'm4',
                    sender: 'agent',
                    text: 'Booked. Alexander Sterling from our private advisory team will host your tour at 2:00 PM. A calendar invitation and security access code have been forwarded.',
                    time: '11:03',
                    status: 'read'
                }
            ]
        },
        codeExample: {
            typescript: `// High-Ticket Lead Scoring & Qualification
await reach.agents.create({
  name: 'Luxury Asset Advisor',
  model: 'latest-gemini-models',
  qualificationSchema: {
    minBudget: 1000000,
    requiredFields: ['timeline', 'financing_type'],
    escalateOnHighIntent: true
  },
  onQualified: async (lead) => {
    await reach.team.assignLead({
      leadId: lead.id,
      assigneeEmail: 'director@advisory.com',
      priority: 'URGENT'
    });
  }
});`,
            python: `# High-Ticket Lead Scoring & Qualification
reach.agents.create(
    name="Luxury Asset Advisor",
    model="latest-gemini-models",
    schema={
        "min_budget": 1000000,
        "required_fields": ["timeline", "financing_type"],
        "escalate_on_high_intent": True
    }
)`,
            curl: `curl -X POST https://api.bytecohortreach.com/v1/agents/qualify \\
  -H "Authorization: Bearer $REACH_API_KEY" \\
  -d '{"model": "latest-gemini-models", "text": "We are all-cash buyers for $2M penthouse"}'`
        }
    }
];

const InteractiveProductWorkbench: React.FC = () => {
    const [activeScenarioId, setActiveScenarioId] = useState<string>('cart-recovery');
    const [activePipelineStep, setActivePipelineStep] = useState<number>(1);
    const [liveMessages, setLiveMessages] = useState<Array<any>>(WORKBENCH_SCENARIOS[0].chatSession.messages);
    const [customInput, setCustomInput] = useState('');
    const [isSimulatingResponse, setIsSimulatingResponse] = useState(false);
    const [activeWorkbenchView, setActiveWorkbenchView] = useState<'pipeline' | 'code'>('pipeline');
    const [codeLanguage, setCodeLanguage] = useState<'typescript' | 'python' | 'curl'>('typescript');
    const [copiedCode, setCopiedCode] = useState(false);

    const currentScenario = WORKBENCH_SCENARIOS.find(s => s.id === activeScenarioId) || WORKBENCH_SCENARIOS[0];

    const handleSelectScenario = (id: string) => {
        setActiveScenarioId(id);
        const match = WORKBENCH_SCENARIOS.find(s => s.id === id) || WORKBENCH_SCENARIOS[0];
        setLiveMessages(match.chatSession.messages);
        setActivePipelineStep(1);
    };

    const handleSendCustomMessage = (textToSend?: string) => {
        const text = textToSend || customInput;
        if (!text.trim()) return;

        const newMsg = {
            id: createMessageId('usr'),
            sender: 'user' as const,
            text: text.trim(),
            time: getFormattedTime()
        };

        setLiveMessages(prev => [...prev, newMsg]);
        setCustomInput('');
        setIsSimulatingResponse(true);

        // Sequence through pipeline steps
        setActivePipelineStep(0);
        setTimeout(() => setActivePipelineStep(1), 300);
        setTimeout(() => setActivePipelineStep(2), 700);
        setTimeout(() => setActivePipelineStep(3), 1100);

        setTimeout(() => {
            const botMsg = {
                id: createMessageId('bot'),
                sender: 'agent' as const,
                text: `Acknowledged: "${text.trim()}". Context grounded via ${currentScenario.pipeline[1].name} and synthesized using the latest Gemini models in 240ms.`,
                time: getFormattedTime(),
                status: 'read' as const
            };
            setLiveMessages(prev => [...prev, botMsg]);
            setIsSimulatingResponse(false);
        }, 1400);
    };

    const handleCopyCode = () => {
        const text = currentScenario.codeExample[codeLanguage];
        navigator.clipboard.writeText(text);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
    };

    return (
        <section id="workbench" className="py-20 container mx-auto px-6 max-w-7xl relative z-10">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 pb-8 border-b border-white/[0.08]">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-[11px] font-mono font-medium text-emerald-400 tracking-wider uppercase">
                            Operational Runtime Simulator
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-tight">
                        Live execution workbench.
                    </h2>
                </div>
                <div className="mt-4 lg:mt-0 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <p className="text-xs text-zinc-400 max-w-md font-normal leading-relaxed">
                        Interact with active scenarios to observe bidirectional Google Sheets sync, reasoning by the latest Gemini models, and instant Meta dispatches.
                    </p>
                </div>
            </div>

            {/* Scenario Segmented Selector */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                {WORKBENCH_SCENARIOS.map((sc) => {
                    const isSelected = sc.id === activeScenarioId;
                    return (
                        <button
                            key={sc.id}
                            type="button"
                            onClick={() => handleSelectScenario(sc.id)}
                            className={`p-4 text-left rounded-xl transition-all duration-200 border ${
                                isSelected 
                                    ? 'bg-[#121520] border-white/20 text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]' 
                                    : 'bg-[#0A0C13]/60 border-white/[0.06] text-zinc-400 hover:text-zinc-200 hover:bg-[#10131B]'
                            }`}
                        >
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 font-medium">
                                    {sc.tag}
                                </span>
                                {isSelected && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                )}
                            </div>
                            <div className="text-sm font-semibold text-white mb-1">
                                {sc.title}
                            </div>
                            <div className="text-xs text-zinc-400 font-normal leading-relaxed line-clamp-2">
                                {sc.description}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Main Workbench Split Studio */}
            <div className="grid grid-cols-1 lg:grid-cols-12 rounded-2xl border border-white/[0.08] bg-[#090A10] overflow-hidden shadow-2xl shadow-black/80">
                
                {/* LEFT: Architectural Node Trace or Code Terminal */}
                <div className="lg:col-span-7 p-6 sm:p-8 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/[0.08] bg-[#090A10]">
                    <div>
                        {/* Tab Switcher: Execution Pipeline vs. Developer SDK Code */}
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/[0.06]">
                            <div className="flex items-center gap-2 bg-[#12141F] p-1 rounded-lg border border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setActiveWorkbenchView('pipeline')}
                                    className={`px-3 py-1 rounded-md text-xs font-mono transition-all ${
                                        activeWorkbenchView === 'pipeline' 
                                            ? 'bg-white/[0.08] text-white font-semibold' 
                                            : 'text-zinc-400 hover:text-zinc-200'
                                    }`}
                                >
                                    Pipeline Execution
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveWorkbenchView('code')}
                                    className={`px-3 py-1 rounded-md text-xs font-mono transition-all ${
                                        activeWorkbenchView === 'code' 
                                            ? 'bg-white/[0.08] text-white font-semibold' 
                                            : 'text-zinc-400 hover:text-zinc-200'
                                    }`}
                                >
                                    Developer SDK
                                </button>
                            </div>

                            {activeWorkbenchView === 'pipeline' ? (
                                <span className="text-[11px] font-mono text-zinc-300">
                                    4 Active Steps • Total Latency ~490ms
                                </span>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 bg-[#12141F] p-0.5 rounded border border-white/[0.04] text-[10px] font-mono">
                                        {(['typescript', 'python', 'curl'] as const).map((lang) => (
                                            <button
                                                key={lang}
                                                type="button"
                                                onClick={() => setCodeLanguage(lang)}
                                                className={`px-2 py-0.5 rounded uppercase ${
                                                    codeLanguage === lang ? 'bg-emerald-500/20 text-emerald-300 font-semibold' : 'text-zinc-400 hover:text-white'
                                                }`}
                                            >
                                                {lang === 'typescript' ? 'TS' : lang === 'python' ? 'PY' : 'cURL'}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCopyCode}
                                        className="p-1.5 rounded hover:bg-white/[0.05] text-zinc-400 hover:text-white transition-colors"
                                        title="Copy code"
                                    >
                                        {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            )}
                        </div>

                        {activeWorkbenchView === 'pipeline' ? (
                            /* Pipeline Vertical Step Flow */
                            <div className="space-y-3 mb-6">
                                {currentScenario.pipeline.map((step, idx) => {
                                    const isActive = activePipelineStep === idx;
                                    return (
                                        <div
                                            key={step.id}
                                            onClick={() => setActivePipelineStep(idx)}
                                            className={`p-4 rounded-xl border transition-all cursor-pointer ${
                                                isActive
                                                    ? 'bg-[#121622] border-white/20 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
                                                    : 'bg-[#0B0D14] border-white/[0.04] hover:border-white/10'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2.5">
                                                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-mono font-bold ${
                                                        isActive ? 'bg-emerald-400 text-zinc-950' : 'bg-white/10 text-zinc-400'
                                                    }`}>
                                                        0{idx + 1}
                                                    </span>
                                                    <span className="text-xs font-semibold text-white">
                                                        {step.name}
                                                    </span>
                                                </div>
                                                <span className="text-[11px] font-mono text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">
                                                    {step.latency}
                                                </span>
                                            </div>

                                            {/* Collapsible JSON payload preview */}
                                            {isActive && (
                                                <div className="mt-3 pt-3 border-t border-white/[0.06] font-mono text-[11px] text-zinc-400">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <span className="text-[10px] uppercase tracking-wider text-zinc-300 font-bold block mb-1">Input Payload</span>
                                                            <pre className="p-2.5 rounded bg-black/50 border border-white/[0.04] overflow-x-auto text-[10px] text-zinc-300">
                                                                {JSON.stringify(step.input, null, 2)}
                                                            </pre>
                                                        </div>
                                                        <div>
                                                            <span className="text-[10px] uppercase tracking-wider text-zinc-300 font-bold block mb-1">Resolved Output</span>
                                                            <pre className="p-2.5 rounded bg-black/50 border border-white/[0.04] overflow-x-auto text-[10px] text-emerald-300">
                                                                {JSON.stringify(step.output, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* Code View */
                            <div className="mb-6 rounded-xl border border-white/[0.06] bg-black/60 p-4 font-mono text-xs overflow-x-auto">
                                <pre className="text-zinc-300 leading-relaxed">
                                    {currentScenario.codeExample[codeLanguage]}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Telemetry Summary Footnote */}
                    <div className="pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs text-zinc-300 font-mono">
                        <div className="flex items-center gap-2">
                            <Shield className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Meta Graph API v21.0 Certified</span>
                        </div>
                        <span>TLS 1.3 / Strict SSRF Validation</span>
                    </div>
                </div>

                {/* RIGHT: Authentic WhatsApp Business Client Terminal */}
                <div className="lg:col-span-5 flex flex-col justify-between bg-[#07080C]">
                    
                    {/* WhatsApp Business Header */}
                    <div className="p-4 px-5 bg-[#0D0F17] border-b border-white/[0.06] flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-emerald-700/60 text-white font-bold flex items-center justify-center text-sm border border-emerald-500/20">
                                {currentScenario.chatSession.avatarLetter}
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-white flex items-center gap-1.5">
                                    <span>{currentScenario.chatSession.customerName}</span>
                                    <CheckCheck className="w-3.5 h-3.5 text-emerald-400" />
                                </div>
                                <div className="text-[11px] text-zinc-400 font-mono">
                                    {currentScenario.chatSession.customerPhone}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 font-medium">
                                Meta Online
                            </span>
                        </div>
                    </div>

                    {/* Chat Messages Feed */}
                    <div className="p-5 flex-1 overflow-y-auto max-h-[380px] min-h-[340px] space-y-3.5 bg-[#08090E]">
                        <div className="text-center my-1">
                            <span className="text-[10px] font-mono text-zinc-300 bg-white/[0.03] border border-white/[0.05] px-2.5 py-1 rounded-full">
                                End-to-end encrypted official business channel
                            </span>
                        </div>

                        {liveMessages.map((msg) => {
                            const isUser = msg.sender === 'user';
                            return (
                                <div 
                                    key={msg.id} 
                                    className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                                >
                                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-xs leading-relaxed ${
                                        isUser 
                                            ? 'bg-[#047857] text-white rounded-br-xs font-normal shadow-sm' 
                                            : 'bg-[#121520] text-zinc-200 border border-white/[0.06] rounded-bl-xs font-normal'
                                    }`}>
                                        <p>{msg.text}</p>
                                        <div className={`flex items-center justify-end gap-1 mt-1 text-[9px] font-mono ${
                                            isUser ? 'text-emerald-100' : 'text-zinc-300'
                                        }`}>
                                            <span>{msg.time}</span>
                                            {msg.status === 'read' && (
                                                <CheckCheck className="w-3 h-3 text-sky-400" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Optional Quick Reply Chips */}
                                    {msg.quickReplies && msg.quickReplies.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {msg.quickReplies.map((replyText: string, rIdx: number) => (
                                                <button
                                                    key={rIdx}
                                                    type="button"
                                                    onClick={() => handleSendCustomMessage(replyText)}
                                                    className="text-[11px] font-medium bg-white/[0.04] hover:bg-white/[0.08] text-emerald-300 border border-emerald-500/20 px-3 py-1 rounded-full transition-all"
                                                >
                                                    {replyText}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {isSimulatingResponse && (
                            <div className="flex items-center gap-1.5 text-zinc-300 text-[11px] font-mono p-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce" />
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce [animation-delay:0.2s]" />
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce [animation-delay:0.4s]" />
                                <span className="ml-1 text-[10px]">Latest Gemini models evaluating context & dispatching...</span>
                            </div>
                        )}
                    </div>

                    {/* Interactive Simulator Input Box */}
                    <div className="p-3.5 bg-[#0D0F17] border-t border-white/[0.06]">
                        <form 
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSendCustomMessage();
                            }}
                            className="flex items-center gap-2"
                        >
                            <input
                                type="text"
                                aria-label="Type simulated customer message"
                                value={customInput}
                                onChange={(e) => setCustomInput(e.target.value)}
                                placeholder="Type a message to simulate customer response..."
                                className="flex-1 bg-[#141722] border border-white/[0.08] text-white text-xs px-3.5 py-2.5 rounded-xl placeholder:text-zinc-400 focus:outline-none focus:border-white/20 transition-all font-normal"
                            />
                            <button
                                type="submit"
                                disabled={!customInput.trim() || isSimulatingResponse}
                                aria-label="Send simulated customer response message"
                                title="Send message"
                                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-30 text-zinc-950 p-2.5 rounded-xl transition-all font-semibold"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </div>

                </div>

            </div>
        </section>
    );
};

// --- FINANCIAL ROI & LABOR COST MODELER ---

const ROI_CURRENCIES = [
    { code: 'USD', symbol: '$', minRate: 15, maxRate: 85, defaultRate: 28 },
    { code: 'EUR', symbol: '€', minRate: 15, maxRate: 85, defaultRate: 26 },
    { code: 'GBP', symbol: '£', minRate: 12, maxRate: 75, defaultRate: 24 },
    { code: 'NGN', symbol: '₦', minRate: 1500, maxRate: 25000, defaultRate: 6000 }
];

const FinancialROIModeler: React.FC = () => {
    const [messagesPerDay, setMessagesPerDay] = useState(3200);
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const currency = ROI_CURRENCIES.find(c => c.code === selectedCurrency) || ROI_CURRENCIES[0];
    const [hourlyWage, setHourlyWage] = useState(currency.defaultRate);
    const [handlingMinutes, setHandlingMinutes] = useState(5);

    const handleCurrencyChange = (code: string) => {
        setSelectedCurrency(code);
        const match = ROI_CURRENCIES.find(c => c.code === code);
        if (match) setHourlyWage(match.defaultRate);
    };

    // 82% automated resolution benchmark
    const monthlyTotalMessages = messagesPerDay * 30;
    const manualHoursNeeded = Math.round((monthlyTotalMessages * handlingMinutes) / 60);
    const hoursSavedMonthly = Math.round(manualHoursNeeded * 0.82);
    const monthlySavings = Math.round(hoursSavedMonthly * hourlyWage);
    const annualSavings = monthlySavings * 12;

    const chartData = [
        { month: 'M1', manualCost: Math.round(manualHoursNeeded * hourlyWage * 0.25), reachCost: Math.round(manualHoursNeeded * hourlyWage * 0.04) },
        { month: 'M3', manualCost: Math.round(manualHoursNeeded * hourlyWage * 0.75), reachCost: Math.round(manualHoursNeeded * hourlyWage * 0.12) },
        { month: 'M6', manualCost: Math.round(manualHoursNeeded * hourlyWage * 1.5), reachCost: Math.round(manualHoursNeeded * hourlyWage * 0.24) },
        { month: 'M9', manualCost: Math.round(manualHoursNeeded * hourlyWage * 2.25), reachCost: Math.round(manualHoursNeeded * hourlyWage * 0.36) },
        { month: 'M12', manualCost: Math.round(manualHoursNeeded * hourlyWage * 3.0), reachCost: Math.round(manualHoursNeeded * hourlyWage * 0.48) }
    ];

    return (
        <section id="roi-modeler" className="py-20 border-t border-white/[0.06] bg-[#07080C] relative">
            <div className="container mx-auto px-6 max-w-7xl relative z-10">
                
                <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span className="text-[11px] font-mono font-medium text-emerald-400 uppercase tracking-wider">
                                Financial Model & Capacity Planner
                            </span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                            Forecast operational cost reduction.
                        </h2>
                    </div>
                    <p className="text-sm text-zinc-400 max-w-md mt-4 lg:mt-0 font-normal leading-relaxed">
                        Calibrate your current support volume against our autonomous triage engine to project annual overhead avoidance.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
                    
                    {/* SLIDERS COLUMN */}
                    <div className="lg:col-span-6 p-6 sm:p-8 rounded-2xl border border-white/[0.08] bg-[#0A0C13] flex flex-col justify-between shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                        <div className="space-y-7">
                            
                            {/* Currency Segment Control */}
                            <div>
                                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-2.5">
                                    Operating Currency
                                </label>
                                <div className="grid grid-cols-4 gap-2 bg-[#12141F] p-1 rounded-lg border border-white/[0.04]">
                                    {ROI_CURRENCIES.map((c) => (
                                        <button
                                            key={c.code}
                                            type="button"
                                            onClick={() => handleCurrencyChange(c.code)}
                                            className={`py-2 rounded-md text-xs font-medium transition-all ${
                                                selectedCurrency === c.code 
                                                    ? 'bg-white text-zinc-950 font-semibold shadow-sm' 
                                                    : 'text-zinc-400 hover:text-white'
                                            }`}
                                        >
                                            {c.symbol} {c.code}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Slider 1: Messages / Day */}
                            <div>
                                <div className="flex justify-between items-baseline mb-2">
                                    <label htmlFor="calc-messages-per-day" className="text-xs font-medium text-zinc-300">Daily Inbound WhatsApp Messages</label>
                                    <span className="text-sm font-mono font-semibold text-white">
                                         {messagesPerDay.toLocaleString()} msgs / day
                                     </span>
                                 </div>
                                 <input
                                     type="range"
                                     id="calc-messages-per-day"
                                     aria-label="Daily Inbound WhatsApp Messages"
                                     min="300"
                                     max="15000"
                                     step="100"
                                     value={messagesPerDay}
                                     onChange={(e) => setMessagesPerDay(parseInt(e.target.value))}
                                     className="w-full accent-emerald-400 h-1.5 bg-zinc-800 rounded-lg cursor-pointer"
                                 />
                                 <div className="flex justify-between text-[11px] font-mono text-zinc-400 mt-1">
                                     <span>300</span>
                                     <span>15,000 / day</span>
                                 </div>
                             </div>

                             {/* Slider 2: Hourly Rate */}
                             <div>
                                 <div className="flex justify-between items-baseline mb-2">
                                     <label htmlFor="calc-hourly-wage" className="text-xs font-medium text-zinc-300">Support / Sales Rep Loaded Hourly Cost</label>
                                     <span className="text-sm font-mono font-semibold text-white">
                                         {currency.symbol}{hourlyWage.toLocaleString()} / hr
                                     </span>
                                 </div>
                                 <input
                                     type="range"
                                     id="calc-hourly-wage"
                                     aria-label="Support or sales representative loaded hourly cost"
                                     min={currency.minRate}
                                     max={currency.maxRate}
                                     step={1}
                                     value={hourlyWage}
                                     onChange={(e) => setHourlyWage(parseInt(e.target.value))}
                                     className="w-full accent-emerald-400 h-1.5 bg-zinc-800 rounded-lg cursor-pointer"
                                 />
                                 <div className="flex justify-between text-[11px] font-mono text-zinc-400 mt-1">
                                     <span>{currency.symbol}{currency.minRate}/hr</span>
                                     <span>{currency.symbol}{currency.maxRate}/hr</span>
                                 </div>
                             </div>

                             {/* Slider 3: Handling Minutes */}
                             <div>
                                 <div className="flex justify-between items-baseline mb-2">
                                     <label htmlFor="calc-handling-minutes" className="text-xs font-medium text-zinc-300">Average Manual Resolution Time</label>
                                     <span className="text-sm font-mono font-semibold text-white">
                                         {handlingMinutes} minutes / ticket
                                     </span>
                                 </div>
                                 <input
                                     type="range"
                                     id="calc-handling-minutes"
                                     aria-label="Average manual resolution time in minutes per ticket"
                                     min="2"
                                     max="15"
                                     step="1"
                                     value={handlingMinutes}
                                     onChange={(e) => setHandlingMinutes(parseInt(e.target.value))}
                                     className="w-full accent-emerald-400 h-1.5 bg-zinc-800 rounded-lg cursor-pointer"
                                 />
                                 <div className="flex justify-between text-[11px] font-mono text-zinc-400 mt-1">
                                     <span>2 mins</span>
                                     <span>15 mins / ticket</span>
                                 </div>
                             </div>

                         </div>

                         <div className="pt-6 mt-6 border-t border-white/[0.06] text-xs text-zinc-400 font-normal leading-relaxed">
                             Calculations reflect documented baseline metrics across 450+ production deployments assuming an 82% tier-1 resolution rate.
                         </div>
                     </div>

                     {/* FINANCIAL OUTPUT & PROJECTION GRAPH */}
                     <div className="lg:col-span-6 p-6 sm:p-8 rounded-2xl border border-white/[0.08] bg-[#0A0C13] flex flex-col justify-between shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                         <div>
                             <div className="grid grid-cols-2 gap-6 pb-6 border-b border-white/[0.06]">
                                 <div>
                                     <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-1">
                                         Monthly Reclaimed Labor
                                     </span>
                                     <div className="text-3xl sm:text-4xl font-mono font-semibold text-white">
                                         {hoursSavedMonthly.toLocaleString()} <span className="text-xs font-normal text-zinc-400 font-sans">hrs/mo</span>
                                     </div>
                                 </div>
                                 <div>
                                     <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-1">
                                         Projected Annual Net Savings
                                     </span>
                                     <div className="text-3xl sm:text-4xl font-mono font-semibold text-emerald-400">
                                         {currency.symbol}{annualSavings.toLocaleString()}
                                     </div>
                                 </div>
                             </div>

                             {/* Chart Area */}
                             <div className="pt-6">
                                 <div className="flex items-center justify-between mb-4">
                                     <span className="text-xs font-medium text-zinc-300 uppercase tracking-wider">
                                         Cumulative 12-Month Expenditure Projection
                                     </span>
                                     <div className="flex items-center gap-4 text-[11px] font-mono">
                                         <span className="flex items-center gap-1 text-zinc-400">
                                             <span className="w-2 h-2 rounded-full bg-zinc-600 inline-block" /> Manual Operations
                                         </span>
                                         <span className="flex items-center gap-1 text-emerald-400">
                                             <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> With Reach
                                         </span>
                                     </div>
                                 </div>

                                 <div className="h-44 w-full min-w-0 min-h-[176px]">
                                     <ResponsiveContainer width="100%" height={176} minWidth={100} minHeight={176}>
                                         <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                             <XAxis dataKey="month" stroke="#71717a" fontSize={10} tickLine={false} />
                                             <YAxis stroke="#71717a" fontSize={10} tickLine={false} />
                                             <ChartTooltip
                                                 formatter={(val: any) => [`${currency.symbol}${Number(val).toLocaleString()}`, '']}
                                                 contentStyle={{ backgroundColor: '#12141F', borderColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                                             />
                                             <Area type="monotone" dataKey="manualCost" name="Manual Support" stroke="#71717a" strokeWidth={1.5} fill="#27272a" fillOpacity={0.2} />
                                             <Area type="monotone" dataKey="reachCost" name="Bytecohort Reach" stroke="#34d399" strokeWidth={1.5} fill="#10b981" fillOpacity={0.15} />
                                         </AreaChart>
                                     </ResponsiveContainer>
                                 </div>
                             </div>
                        </div>

                        <div className="pt-6 mt-6 border-t border-white/[0.06] flex items-center justify-between">
                            <div>
                                <span className="text-xs text-zinc-400 block font-normal">Ready to benchmark on your company data?</span>
                                <span className="text-[11px] text-zinc-300">Deploy instantly in sandbox mode with zero card requirement.</span>
                            </div>
                            <Link
                                href="/app"
                                className="px-5 py-2.5 rounded-lg bg-white text-zinc-950 font-semibold text-xs uppercase tracking-wider hover:bg-zinc-200 transition-all flex items-center gap-2 shrink-0"
                            >
                                <span>Deploy Free</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>

                </div>

            </div>
        </section>
    );
};

// --- ENTERPRISE ARCHITECTURAL ADVANTAGE & COMPARISON TABLE ---

const TECHNICAL_COMPARISON = [
    {
        dimension: "Transport Protocol & Gateway",
        reach: "Official Meta Graph API v21.0 Direct Webhook Gateway",
        scrapers: "Headless Chromium browser injection & Puppeteer hooks",
        traditional: "Official API, but charges heavy per-conversation markup penalties"
    },
    {
        dimension: "Account Ban Risk Exposure",
        reach: "0% — Fully certified Meta Business Cloud infrastructure",
        scrapers: "High — Subject to immediate algorithmic hardware/phone bans",
        traditional: "0% — Verified infrastructure"
    },
    {
        dimension: "Google Sheets Synchronization",
        reach: "Native 2-way webhook engine (read, write, append, search < 280ms)",
        scrapers: "None or manual daily CSV exports required",
        traditional: "Requires paid third-party automation tools (Zapier/Make)"
    },
    {
        dimension: "AI Agent Intelligence Tier",
        reach: "Multi-turn reasoning and strict JSON schema adherence powered by the latest Gemini models",
        scrapers: "Rigid keyword regex or legacy completion wrappers",
        traditional: "Basic rule-based decision trees or costly bolt-on add-ons"
    },
    {
        dimension: "Webhook Dispatch Latency (p99)",
        reach: "< 280ms end-to-end event execution",
        scrapers: "8 to 15 seconds due to browser DOM evaluation",
        traditional: "1.2 to 3.5 seconds across multi-hop legacy servers"
    },
    {
        dimension: "Data Security & Compliance",
        reach: "SOC-2 Type II ready, TLS 1.3, strict SSRF & phone masking",
        scrapers: "Critical vulnerability: plain-text WhatsApp Web session tokens stored locally",
        traditional: "Standard enterprise compliance"
    }
];

const ArchitecturalComparisonSection: React.FC = () => {
    return (
        <section id="comparison" className="py-20 container mx-auto px-6 max-w-7xl relative z-10 border-t border-white/[0.06]">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-[11px] font-mono font-medium text-emerald-400 uppercase tracking-wider">
                            Technical Verification Matrix
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                        Architecture compared.
                    </h2>
                </div>
                <p className="text-sm text-zinc-400 max-w-md mt-4 lg:mt-0 font-normal leading-relaxed">
                    Compare native Meta Cloud integration with unofficial browser automation methods and legacy support software.
                </p>
            </div>

            <div className="rounded-2xl border border-white/[0.08] bg-[#0A0C13] overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-white/[0.08] bg-[#0E1018]">
                            <th className="p-5 text-xs font-mono font-medium text-zinc-400 uppercase tracking-wider w-1/4">
                                Architectural Dimension
                            </th>
                            <th className="p-5 text-xs font-mono font-semibold text-emerald-400 bg-emerald-400/[0.04] border-x border-white/[0.08] w-2/5">
                                Bytecohort Reach (Official Meta Cloud)
                            </th>
                            <th className="p-5 text-xs font-mono font-medium text-zinc-300 uppercase tracking-wider w-1/3">
                                Unofficial Browser Extensions / Scrapers
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06] text-xs">
                        {TECHNICAL_COMPARISON.map((row, idx) => (
                            <tr key={idx} className="hover:bg-white/[0.015] transition-colors">
                                <td className="p-5 font-medium text-white">
                                    {row.dimension}
                                </td>
                                <td className="p-5 bg-emerald-400/[0.02] border-x border-white/[0.08] text-zinc-200">
                                    <div className="flex items-start gap-2.5">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                        <span className="leading-relaxed font-normal">{row.reach}</span>
                                    </div>
                                </td>
                                <td className="p-5 text-zinc-400 leading-relaxed font-normal">
                                    {row.scrapers}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

// --- SYSTEM CAPABILITIES: CLEAN BENTO GRID ---

const SystemCapabilitiesSection: React.FC = () => {
    const capabilities = [
        {
            title: "Autonomous Intelligence Powered by Latest Gemini Models",
            description: "Equip operational workflows with the latest Gemini models. Enforce strict JSON output schemas, conversational guardrails, and customer sentiment classification.",
            tag: "Multi-Turn Reasoning",
            colSpan: "lg:col-span-8",
            preview: (
                <div className="p-4 bg-[#08090E] rounded-xl border border-white/[0.06] font-mono text-[11px] text-zinc-400 space-y-2">
                    <div className="flex items-center justify-between text-zinc-300 pb-2 border-b border-white/[0.04]">
                        <span>POST /api/agents/v1/reason</span>
                        <span className="text-emerald-400">200 OK • 240ms</span>
                    </div>
                    <div className="text-zinc-300">
                        &#123; "intent": "quote_inquiry", "confidence": 0.99, "action": "trigger_sheet_lookup", "model": "latest-gemini-models" &#125;
                    </div>
                </div>
            )
        },
        {
            title: "Two-Way Google Sheets Engine",
            description: "Native synchronization to read, append, update, and search rows in milliseconds without expensive third-party connector tools.",
            tag: "Real-Time CRM",
            colSpan: "lg:col-span-4",
            preview: (
                <div className="p-4 bg-[#08090E] rounded-xl border border-white/[0.06] flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-xs">
                        CSV
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-white">Inbound_Leads_Q3.csv</div>
                        <div className="text-[11px] text-emerald-400 font-mono">Synced 240ms ago</div>
                    </div>
                </div>
            )
        },
        {
            title: "Official Meta Cloud API Dispatch",
            description: "Deliver verified marketing templates and customer notifications at 80+ messages per second with Meta-backed deliverability guarantees.",
            tag: "High-Throughput Gateway",
            colSpan: "lg:col-span-4",
            preview: (
                <div className="p-4 bg-[#08090E] rounded-xl border border-white/[0.06] space-y-2">
                    <div className="flex justify-between text-xs">
                        <span className="text-zinc-400">Template Approval</span>
                        <span className="text-emerald-400 font-mono font-semibold">Active Tier</span>
                    </div>
                    <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="w-full h-full bg-emerald-400" />
                    </div>
                </div>
            )
        },
        {
            title: "Unified Team Inbox & Live Handoff",
            description: "Seamlessly transition conversations from automated agents to human support staff with full thread history, internal notes, and collision prevention.",
            tag: "Human-In-The-Loop",
            colSpan: "lg:col-span-8",
            preview: (
                <div className="p-4 bg-[#08090E] rounded-xl border border-white/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex -space-x-2">
                            {['A', 'M', 'S'].map((init, i) => (
                                <div key={i} className="w-7 h-7 rounded-full bg-zinc-800 border-2 border-[#08090E] text-[10px] font-bold text-white flex items-center justify-center">
                                    {init}
                                </div>
                            ))}
                        </div>
                        <span className="text-xs text-zinc-300">3 Operators Active • Round-Robin Routing</span>
                    </div>
                    <span className="text-[11px] font-mono text-zinc-400 bg-white/[0.04] px-2.5 py-1 rounded">
                        SLA: &lt; 60s
                    </span>
                </div>
            )
        }
    ];

    return (
        <section id="features" className="py-20 container mx-auto px-6 max-w-7xl relative z-10 border-t border-white/[0.06]">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-[11px] font-mono font-medium text-emerald-400 uppercase tracking-wider">
                            Core Platform Capabilities
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                        Engineered for mission-critical operations.
                    </h2>
                </div>
                <p className="text-sm text-zinc-400 max-w-md mt-4 lg:mt-0 font-normal leading-relaxed">
                    A unified stack designed to handle high-volume conversational workloads with strict observability.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {capabilities.map((cap, i) => (
                    <div
                        key={i}
                        className={`${cap.colSpan} p-6 sm:p-8 rounded-2xl border border-white/[0.08] bg-[#0A0C13] flex flex-col justify-between shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]`}
                    >
                        <div className="mb-6">
                            <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 font-medium block mb-2">
                                {cap.tag}
                            </span>
                            <h3 className="text-xl font-semibold text-white tracking-tight mb-2">
                                {cap.title}
                            </h3>
                            <p className="text-sm text-zinc-400 font-normal leading-relaxed">
                                {cap.description}
                            </p>
                        </div>
                        {cap.preview}
                    </div>
                ))}
            </div>
        </section>
    );
};

// --- STRATEGY GUIDES & PLAYBOOKS (Whitepaper Format) ---

const EngineeringPlaybooksSection: React.FC = () => {
    const playbooks = [
        {
            title: "WhatsApp Automation in Nigeria: Complete Guide to Official Meta API, AI Agents & Naira Billing",
            desc: "The master playbook for Nigerian businesses scaling WhatsApp sales, Paystack Naira payments, two-way Google Sheets sync, and 24/7 Gemini AI agents.",
            tag: "Nigeria Pillar Guide 🇳🇬",
            readTime: "8 min read",
            href: "/whatsapp-automation-nigeria"
        },
        {
            title: "Official Meta WhatsApp API Pricing in Nigeria (2026 Naira Breakdown vs. Wati & Twilio)",
            desc: "Interactive Naira (₦) cost & ROI calculator comparing local Paystack billing against USD virtual card brokers and per-message markups.",
            tag: "Nigeria Pricing",
            readTime: "8 min read",
            href: "/blog/whatsapp-api-pricing-nigeria"
        },
        {
            title: "Why Wasender & GBWhatsApp Get +234 Numbers Banned (And How to Migrate to Official Meta API)",
            desc: "Understand how Meta detects browser automation scripts and how to migrate your existing +234 SIM to 100% anti-ban Cloud API in 5 minutes.",
            tag: "Anti-Ban Compliance",
            readTime: "7 min read",
            href: "/blog/wasender-gbwhatsapp-ban-official-meta-api-nigeria"
        },
        {
            title: "Automate WhatsApp Order Confirmations, Paystack Receipts & Delivery Alerts with Google Sheets",
            desc: "Copy production-ready Meta Utility templates and Gemini AI system prompts for Nigerian e-commerce, logistics, and real estate teams.",
            tag: "Order Automation",
            readTime: "9 min read",
            href: "/blog/paystack-whatsapp-order-automation-google-sheets-nigeria"
        }
    ];

    return (
        <section id="playbooks" className="py-20 container mx-auto px-6 max-w-7xl relative z-10 border-t border-white/[0.06]">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-[11px] font-mono font-medium text-emerald-400 uppercase tracking-wider">
                            Engineering Documentation & Field Guides
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                        Battle-tested implementation playbooks.
                    </h2>
                </div>
                <Link
                    href="/blog"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider mt-4 lg:mt-0"
                >
                    <span>Browse All Guides</span>
                    <ArrowRight className="w-4 h-4" />
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {playbooks.map((p, idx) => (
                    <Link
                        key={idx}
                        href={p.href}
                        className="group p-6 rounded-2xl border border-white/[0.08] bg-[#0A0C13] hover:bg-[#10131E] hover:border-white/20 transition-all flex flex-col justify-between shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]"
                    >
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">
                                    {p.tag}
                                </span>
                                <span className="text-[11px] font-mono text-zinc-300 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {p.readTime}
                                </span>
                            </div>
                            <h3 className="text-base font-semibold text-white group-hover:text-emerald-300 transition-colors leading-snug mb-3">
                                {p.title}
                            </h3>
                            <p className="text-xs text-zinc-400 font-normal leading-relaxed">
                                {p.desc}
                            </p>
                        </div>
                        <div className="pt-4 mt-6 border-t border-white/[0.06] flex items-center justify-between text-xs font-medium text-zinc-400 group-hover:text-white">
                            <span>Read Playbook</span>
                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
};

// --- ENTERPRISE FAQ SECTION ---

const EnterpriseFAQSection: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const faqs = [
        {
            q: "Do I need a verified Meta Business Manager account to start?",
            a: "You can immediately begin testing in our developer sandbox without business verification using test numbers. For live production broadcasts to customer lists, Meta requires standard Business Manager verification to link your official phone number, which ensures zero spam penalties."
        },
        {
            q: "How does the two-way Google Sheets webhook synchronization function?",
            a: "Our dispatch service establishes secure bi-directional polling and webhook listeners against your spreadsheets. When a new row is logged with a phone number, Reach extracts variables, interpolates your Meta templates, routes the conversation through guardrails powered by the latest Gemini models, and writes status logs back to the sheet in under 280ms."
        },
        {
            q: "Is there any risk of WhatsApp number bans or account suspensions?",
            a: "Zero. Because Bytecohort Reach integrates exclusively with Meta's official Cloud API infrastructure (rather than unofficial browser scrapers or headless tools), your sending activity adheres 100% to Meta's Business Policies. As long as users have opted in, your line remains in complete good standing."
        },
        {
            q: "How does the hybrid human handoff mechanism operate?",
            a: "Handoff can be triggered either conditionally (when the AI detects negative sentiment or high-priority billing inquiries) or via explicit workflow routing nodes. The customer thread transitions instantly to your team inbox with full conversation history and custom variables preserved."
        },
        {
            q: "What rate limits apply to bulk broadcast dispatches?",
            a: "Throughput scales automatically with Meta's official messaging tiers. Production tiers begin at 1,000 unique business-initiated conversations per 24 hours and automatically graduate to 10,000, 100,000, and unlimited tiers as your quality score remains high."
        }
    ];

    return (
        <section id="faq" className="py-20 container mx-auto px-6 max-w-4xl relative z-10 border-t border-white/[0.06]">
            <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-[11px] font-mono font-medium text-emerald-400 uppercase tracking-wider">
                        Frequently Asked Technical Inquiries
                    </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                    Clear answers for operations leaders.
                </h2>
            </div>

            <div className="space-y-3">
                {faqs.map((faq, i) => {
                    const isOpen = openIndex === i;
                    return (
                        <div
                            key={i}
                            className="rounded-xl border border-white/[0.08] bg-[#0A0C13] overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
                        >
                            <button
                                type="button"
                                onClick={() => setOpenIndex(isOpen ? null : i)}
                                className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                            >
                                <span className="text-sm font-medium text-white pr-4">{faq.q}</span>
                                <ChevronRight className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                            </button>
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-5 pb-5 pt-1 text-xs text-zinc-400 leading-relaxed font-normal border-t border-white/[0.04]">
                                            {faq.a}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// --- FINAL CONVERSION CALL TO ACTION ---

const FinalCTA: React.FC = () => {
    return (
        <section className="py-20 container mx-auto px-6 max-w-7xl relative z-10 border-t border-white/[0.06]">
            <div className="rounded-3xl border border-white/[0.1] bg-[#0B0D15] p-8 sm:p-14 text-center relative overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]">
                <div className="max-w-2xl mx-auto relative z-10">
                    <span className="text-xs font-mono font-medium uppercase tracking-wider text-emerald-400 block mb-3">
                        Deploy in minutes
                    </span>
                    <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white mb-5 leading-tight">
                        Start automating customer triage on WhatsApp today.
                    </h2>
                    <p className="text-sm sm:text-base text-zinc-400 mb-8 font-normal leading-relaxed">
                        Join leading operations and customer teams deploying autonomous workflows powered by the latest Gemini models, sheet-synced campaigns, and unified team inbox routing.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link
                            href="/app"
                            className="w-full sm:w-auto px-7 py-3.5 rounded-lg bg-white text-zinc-950 font-semibold text-xs sm:text-sm uppercase tracking-wider hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 shadow-lg shadow-white/5"
                        >
                            <span>Start 7-Day Free Trial</span>
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            href="/demo"
                            className="w-full sm:w-auto px-7 py-3.5 rounded-lg border border-white/10 text-white font-medium text-xs sm:text-sm hover:bg-white/[0.04] transition-all flex items-center justify-center gap-2"
                        >
                            <MessageSquare className="w-4 h-4 text-emerald-400" />
                            <span>Test Live WhatsApp Demo</span>
                        </Link>
                    </div>

                    <div className="mt-8 pt-8 border-t border-white/[0.06] flex flex-wrap items-center justify-center gap-6 text-xs text-zinc-300 font-mono">
                        <span>Meta Certified Cloud Infrastructure</span>
                        <span>•</span>
                        <span>SOC-2 Type II Compliance</span>
                        <span>•</span>
                        <span>Zero Extension Ban Risk</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- MAIN CONTROLLER COMPONENT ---

const PublicHomePage: React.FC = () => {
    const { path } = useRouter();

    useEffect(() => {
        const handleHashScroll = () => {
            const hash = window.location.hash;
            const targetId = hash.replace(/^#\/?/, '').split('?')[0];
            if (targetId) {
                const element = document.getElementById(targetId);
                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 200);
                }
            }
        };

        handleHashScroll();
        window.addEventListener('hashchange', handleHashScroll);
        return () => {
            window.removeEventListener('hashchange', handleHashScroll);
        };
    }, [path]);

    return (
        <div className="font-sans antialiased text-zinc-200 relative min-h-screen bg-[#07080B] selection:bg-emerald-500/20 selection:text-emerald-200 overflow-x-hidden">
            <SEO 
                title="Bytecohort Reach | Enterprise WhatsApp Automation & AI Platform"
                description="The verified WhatsApp automation platform. Deploy autonomous workflows powered by the latest Gemini models, synchronize two-way Google Sheets pipelines, and unify multi-agent inbox triage on official Meta Cloud API infrastructure."
                keywords="whatsapp automation, bulk sender, ai agent, official meta business api, sheets sync"
            />

            <ExecutiveBackground />

            {/* --- TOP OPERATIONAL STATUS BAR --- */}
            <TopStatusBanner />

            {/* --- TOP NAVIGATION BAR --- */}
            <header className="sticky top-0 left-0 right-0 z-50 transition-all duration-200 backdrop-blur-md border-b border-white/[0.08] bg-[#07080B]/85">
                <nav className="container mx-auto px-6 h-16 flex justify-between items-center max-w-7xl">
                    <Link href="/" className="flex items-center space-x-2">
                        <Logo className="h-6 w-auto" />
                    </Link>

                    <div className="hidden md:flex items-center space-x-6 text-xs font-medium text-zinc-400">
                        <a href="#workbench" className="hover:text-white transition-colors">Workbench</a>
                        <a href="#roi-modeler" className="hover:text-white transition-colors">ROI Modeler</a>
                        <a href="#features" className="hover:text-white transition-colors">Capabilities</a>
                        <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:text-emerald-300 transition-colors font-semibold">Nigeria 🇳🇬</Link>
                        <Link href="/blog" className="hover:text-white transition-colors">Playbooks</Link>
                        <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                        <Link href="/docs" className="hover:text-white transition-colors">Docs</Link>
                    </div>

                    <div className="flex items-center space-x-4">
                        <Link 
                            href="/app" 
                            className="text-xs font-medium text-zinc-400 hover:text-white transition-colors hidden sm:block"
                        >
                            Sign In
                        </Link>
                        <Link 
                            href="/app" 
                            className="px-4 py-2 rounded-lg bg-white text-zinc-950 hover:bg-zinc-200 transition-all font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2)]"
                        >
                            <span>Deploy Free</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </nav>
            </header>

            <main className="relative z-10">
                
                {/* --- HERO SECTION: AUTHORITATIVE & PRECISE --- */}
                <section className="pt-24 pb-16 px-6 container mx-auto text-center max-w-5xl">
                    
                    {/* Meta Verification Credential Tag */}
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] mb-8">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-xs font-mono font-medium text-zinc-300">
                            Meta Cloud API Infrastructure • Graph API v21.0
                        </span>
                    </div>

                    {/* Headline */}
                    <h1 className="text-4xl sm:text-6xl lg:text-7xl font-semibold text-white tracking-[-0.03em] leading-[1.08] mb-6">
                        The WhatsApp Automation Platform for High-Velocity Operations.
                    </h1>

                    {/* Subtitle */}
                    <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 font-normal leading-relaxed">
                        Deploy autonomous workflows powered by the latest Gemini models, orchestrate bidirectional Google Sheet pipelines, and coordinate team inbox triage — on official Meta Cloud infrastructure with zero ban risk.
                    </p>

                    {/* Conversion Triggers */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-10">
                        <Link
                            href="/app"
                            className="w-full sm:w-auto px-7 py-3.5 rounded-lg bg-white text-zinc-950 font-semibold text-xs sm:text-sm uppercase tracking-wider hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 shadow-lg shadow-white/5"
                        >
                            <span>Deploy in Sandbox</span>
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            href="/demo"
                            className="w-full sm:w-auto px-7 py-3.5 rounded-lg border border-white/10 text-white font-medium text-xs sm:text-sm hover:bg-white/[0.04] transition-all flex items-center justify-center gap-2"
                        >
                            <MessageSquare className="w-4 h-4 text-emerald-400" />
                            <span>Test Live on WhatsApp</span>
                        </Link>
                    </div>

                    {/* Trust Proof Bar */}
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-mono text-zinc-300">
                        <span className="text-emerald-400 font-medium">⚡ 60-Sec 1-Click Meta Connect</span>
                        <span>•</span>
                        <span>Official Meta Graph API</span>
                        <span>•</span>
                        <span>SOC-2 Type II Ready</span>
                        <span>•</span>
                        <span>&lt; 280ms Webhook Dispatch</span>
                        <span>•</span>
                        <span>No Credit Card Required</span>
                    </div>

                </section>

                {/* --- STATS STRIP --- */}
                <EnterpriseStatsStrip />

                {/* --- LIVE INTERACTIVE WORKBENCH --- */}
                <InteractiveProductWorkbench />

                {/* --- CAPABILITIES BENTO --- */}
                <SystemCapabilitiesSection />

                {/* --- FINANCIAL ROI MODELER --- */}
                <FinancialROIModeler />

                {/* --- TECHNICAL COMPARISON MATRIX --- */}
                <ArchitecturalComparisonSection />

                {/* --- PLAYBOOKS & GUIDES --- */}
                <EngineeringPlaybooksSection />

                {/* --- ENTERPRISE FAQ --- */}
                <EnterpriseFAQSection />

                {/* --- FINAL CTA --- */}
                <FinalCTA />

                {/* --- FOOTER --- */}
                <footer className="border-t border-white/[0.06] py-14 bg-[#050608]">
                    <div className="container mx-auto px-6 max-w-7xl">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-10 border-b border-white/[0.04]">
                            <Logo className="h-5 w-auto" />
                            <div className="flex flex-wrap justify-center gap-6 text-xs text-zinc-400 font-normal">
                                <Link href="/blog" className="hover:text-white transition-colors">Engineering Blog</Link>
                                <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium">WhatsApp Automation Nigeria 🇳🇬</Link>
                                <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                                <Link href="/docs" className="hover:text-white transition-colors">Documentation</Link>
                                <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                                <Link href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
                                <a href="mailto:support@bytecohortreach.com" className="hover:text-white transition-colors">Support</a>
                            </div>
                        </div>
                        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-zinc-400">
                            <p>&copy; {new Date().getFullYear()} Bytecohort Reach. Enterprise WhatsApp Automation Platform.</p>
                            <p>Official Meta Cloud API Infrastructure Partner.</p>
                        </div>
                    </div>
                </footer>

            </main>

            <LandingPageChatbot />
        </div>
    );
};

export default PublicHomePage;
